import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { PushAttemptView } from "../components/sessionPush/PushAttemptView";
import { PageHeader } from "../components/shell/PageHeader";
import { EmptyState, ErrorNotice, SkeletonRows } from "../components/ui/Feedback";
import { Freshness } from "../components/ui/Freshness";
import { SelectField } from "../components/ui/Input";
import { Pill } from "../components/ui/Status";
import { Cell, Pagination, TableFrame, TableHead, TableRow } from "../components/ui/Table";
import type { Column } from "../components/ui/Table";
import { Metric, MicroLabel } from "../components/ui/Text";
import { get, getPage } from "../lib/api";
import { count, since } from "../lib/format";
import {
  ATTEMPT_OUTCOMES,
  answerHeadline,
  attemptDuration,
  attemptOutcomeLabel,
  attemptTone,
} from "../lib/sessionPushes";
import type { AppCatalog, AppSummary, SessionPushAttempt } from "../lib/types";
import { useViewParam } from "../lib/useViewParam";

/**
 * The push log: every request a session push made, and what the partner said.
 *
 * The App page says where sessions go and the session says whether it got there;
 * this is the wire between them, for the day a partner says "we never got it" or
 * "you sent the wrong plate". A row opens the request and the answer whole.
 *
 * Kept for 90 days, then pruned (`PUSH_LOG_RETENTION_DAYS`), so the masthead
 * total covers that window, not everything ever sent.
 */

/** Each poll also counts over the whole log; requests arrive at most 40 a minute. */
const POLL_MS = 30_000;

const COLUMNS: readonly Column[] = [
  { key: "when", label: "When", width: "12%" },
  { key: "app", label: "App", width: "18%" },
  { key: "session", label: "Session", width: "24%" },
  { key: "answer", label: "Answer" },
  { key: "took", label: "Took", width: "10%" },
];

export function SessionPushes() {
  const [params, setParams] = useSearchParams();
  const [viewing, setViewing] = useViewParam("attempt");

  const appId = params.get("appId") ?? "";
  const outcome = params.get("outcome") ?? "";
  /** From a session's "Pushed to"; a uuid nobody types, so it is shown as something to clear. */
  const sessionId = params.get("sessionId") ?? "";
  const page = positiveInt(params.get("page")) ?? 1;

  const attempts = useQuery({
    queryKey: ["session-pushes", { appId, outcome, sessionId, page }],
    queryFn: () =>
      getPage<SessionPushAttempt>("/session-pushes", {
        query: { appId, outcome, sessionId, page },
      }),
    refetchInterval: POLL_MS,
    placeholderData: (previous) => previous,
  });
  /**
   * Requests that got no usable answer, whatever the table is filtered to.
   * Refusals (422, 401) are not counted here: they park the push and raise an
   * alert, and the outcome filter shows them.
   */
  const failed = useQuery({
    queryKey: ["session-pushes", "failed-count", appId],
    queryFn: () =>
      getPage<SessionPushAttempt>("/session-pushes", {
        query: { appId, outcome: "failed", limit: 1 },
      }),
    refetchInterval: POLL_MS,
  });
  const apps = useQuery({
    queryKey: ["apps"],
    queryFn: () => getPage<AppSummary>("/apps", { query: { limit: 100 } }),
  });
  const catalog = useQuery({
    queryKey: ["apps-catalog"],
    queryFn: () => get<AppCatalog>("/apps/catalog"),
    staleTime: Number.POSITIVE_INFINITY,
  });

  const rows = attempts.data?.rows ?? [];
  const isFiltered = appId !== "" || outcome !== "" || sessionId !== "";
  const failedCount = failed.data?.meta?.total ?? 0;

  function setFilter(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value === "") next.delete(key);
    else next.set(key, value);
    next.delete("page");
    setParams(next, { replace: true });
  }

  return (
    <>
      <PageHeader
        eyebrow="Configuration"
        title="Session pushes"
        metrics={
          <>
            <Metric
              label="Requests"
              value={count(attempts.data?.meta?.total)}
              hint="matching filters"
            />
            <Metric
              label="Failed"
              value={count(failed.data?.meta?.total)}
              hint="no answer, or one we could not read"
              tone={failedCount > 0 ? "signal" : "muted"}
            />
          </>
        }
        freshness={
          <Freshness
            updatedAt={attempts.dataUpdatedAt}
            isFetching={attempts.isFetching}
            intervalMs={POLL_MS}
          />
        }
        lede="Every request a session push sent to a partner, and the answer it got back. Open one to see both bodies exactly as they went over the wire; the bearer token is never kept. Requests are kept for 90 days."
      />

      <form
        onSubmit={(event) => event.preventDefault()}
        className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2 lg:grid-cols-4"
      >
        <SelectField
          label="App"
          value={appId}
          onChange={(event) => setFilter("appId", event.target.value)}
        >
          <option value="">Any</option>
          {/* Every app, not only those pushing now: one whose push was removed keeps its history. */}
          {(apps.data?.rows ?? []).map((app) => (
            <option key={app.id} value={app.id}>
              {app.name}
            </option>
          ))}
        </SelectField>
        <SelectField
          label="Outcome"
          value={outcome}
          onChange={(event) => setFilter("outcome", event.target.value)}
        >
          <option value="">Any</option>
          {ATTEMPT_OUTCOMES.map((one) => (
            <option key={one} value={one}>
              {attemptOutcomeLabel(one)}
            </option>
          ))}
        </SelectField>
      </form>

      {sessionId === "" ? null : (
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 border-l-2 border-signal bg-signal-wash/40 px-4 py-3">
          <MicroLabel>Filtered to one session</MicroLabel>
          <span className="text-body text-ink">
            {rows[0] === undefined
              ? "This session"
              : `${rows[0].session.chargePointId} · ${rows[0].plate ?? "no plate"}`}{" "}
            — showing only its requests.
          </span>
          <button
            type="button"
            onClick={() => setFilter("sessionId", "")}
            className="text-micro font-semibold uppercase tracking-[0.11em] text-signal underline decoration-1 underline-offset-4 hover:no-underline"
          >
            Show all
          </button>
        </div>
      )}

      {attempts.isError ? (
        <ErrorNotice error={attempts.error} onRetry={() => void attempts.refetch()} />
      ) : null}

      <div className="flex flex-col gap-3">
        <TableFrame>
          <TableHead columns={COLUMNS} />
          {attempts.isPending ? (
            <SkeletonRows rows={8} columns={COLUMNS.length} />
          ) : (
            <tbody>
              {rows.map((row, index) => (
                <TableRow
                  key={row.id}
                  index={index}
                  onActivate={() => setViewing(row.id)}
                  isFaulted={attemptTone(row.outcome) === "faulted"}
                >
                  <Cell muted>
                    <time
                      dateTime={row.attemptedAt}
                      title={new Date(row.attemptedAt).toLocaleString()}
                    >
                      {since(row.attemptedAt)}
                    </time>
                  </Cell>
                  <Cell>
                    <Link
                      to={`/apps/${encodeURIComponent(row.app.id)}`}
                      onClick={(event) => event.stopPropagation()}
                      className="font-medium text-ink underline decoration-rule underline-offset-2 hover:decoration-signal"
                    >
                      {row.app.name}
                    </Link>
                  </Cell>
                  <Cell>
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium text-ink">{row.plate ?? "no plate"}</span>
                      <span className="font-mono text-micro tracking-normal normal-case text-ink-faint">
                        {row.session.chargePointId}
                      </span>
                    </div>
                  </Cell>
                  <Cell>
                    <div className="flex flex-wrap items-baseline gap-2">
                      <Pill tone={attemptTone(row.outcome)}>
                        {attemptOutcomeLabel(row.outcome)}
                      </Pill>
                      <span className="text-data text-ink-faint">{answerHeadline(row)}</span>
                    </div>
                  </Cell>
                  <Cell muted>{attemptDuration(row.durationMs)}</Cell>
                </TableRow>
              ))}
            </tbody>
          )}
        </TableFrame>

        {!attempts.isPending && rows.length === 0 ? (
          <EmptyState
            title={
              isFiltered ? "No requests match these filters." : "No session has been pushed yet."
            }
            detail="A row appears each time a push sends a session to a partner. A session that was skipped (no card, no energy) or is waiting for its state of charge sends nothing, so it is not here."
          />
        ) : null}

        <Pagination
          meta={attempts.data?.meta}
          shown={rows.length}
          onPage={(next) => {
            const nextParams = new URLSearchParams(params);
            if (next <= 1) nextParams.delete("page");
            else nextParams.set("page", String(next));
            setParams(nextParams);
          }}
        />
      </div>

      <PushAttemptView
        attemptId={viewing}
        timeZone={catalog.data?.timeZone ?? "UTC"}
        open={viewing !== ""}
        onOpenChange={(open) => {
          if (!open) setViewing("");
        }}
      />
    </>
  );
}

function positiveInt(raw: string | null): number | undefined {
  if (raw === null || !/^\d+$/.test(raw)) return undefined;
  const value = Number(raw);
  return value >= 1 ? value : undefined;
}
